import { Controller, useForm } from 'react-hook-form';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import { FormControl, InputLabel, MenuItem, OutlinedInput, Select, Box, Stack, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';

import { useCreateContract, useGetContractByShippingService } from 'src/use-cases/serviceProvider/useServiceProvider';
import { openToast } from 'src/use-cases/toast/useToast';
import IconInfo from 'src/assets/images/icons/info-circle-grey-3.svg';
import { ContractDto } from 'src/generated/services/TFinancialApi';
import { FormContainer } from '../forms/FormContainer';
import { ModalConfirm } from '../../molecules/modal';

interface ValidityPeriodOption {
  label: string;
  value: string;
}

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormLabel-root': {
    fontSize: '16px',

    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiInputBase-input ': {
    fontSize: '16px',

    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  backgroundColor: '#ffffff',

  '& .MuiSelect-select': {
    fontSize: '16px',

    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '16px',

  [theme.breakpoints.down(1520)]: {
    fontSize: '14px',
  },
}));

interface IModalProps {
  shippingServiceId: string;
  open: boolean;
  setOpen: (value: boolean) => void;
}

export const CreateValidityPeriodModal = ({ shippingServiceId, open, setOpen }: IModalProps) => {
  // State for validity period options
  const [options, setOptions] = useState<ValidityPeriodOption[]>([]);
  const formContext = useForm({
    defaultValues: {
      shippingServiceId,
      startDate: new Date(),
      endDate: new Date(),
      validityPeriodId: null,
    },
  });

  // Watch for changes to validityPeriodId to fetch details
  const { watch } = formContext;
  const selectedValidityPeriodId = watch('validityPeriodId');

  const { control, handleSubmit, reset } = formContext;
  const { mutateAsync: createValidityPeriod } = useCreateContract();

  // Fetch contracts for the shipping service to populate options
  const { data: contractsData, isLoading: isLoadingContracts } = useGetContractByShippingService(shippingServiceId, {
    refetchOnWindowFocus: false,
    enabled: !!shippingServiceId && open,
  });

  // Get selected contract data
  const [selectedContract, setSelectedContract] = useState<ContractDto | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Update selected contract when validityPeriodId changes
  useEffect(() => {
    if (selectedValidityPeriodId && contractsData?.data) {
      setIsLoadingDetails(true);
      // Find the selected contract from contractsData
      const contract = contractsData.data.find((c) => c.contractFlowId === selectedValidityPeriodId);
      if (contract) {
        setSelectedContract(contract);
      }
      setIsLoadingDetails(false);
    } else {
      setSelectedContract(null);
    }
  }, [selectedValidityPeriodId, contractsData?.data]);

  // Update options when contracts data changes
  useEffect(() => {
    if (contractsData?.data && contractsData.data.length > 0) {
      const newOptions = contractsData.data.map((contract: ContractDto) => ({
        label:
          contract.startDate && contract.endDate
            ? `${format(new Date(contract.startDate), 'dd-MMM-yyyy')} to ${format(new Date(contract.endDate), 'dd-MMM-yyyy')}${
                contract.code ? ` (${contract.code})` : ''
              }`
            : `Contract ${contract.contractFlowId}`,
        value: contract.contractFlowId || '',
      }));
      setOptions(newOptions);
    }
  }, [contractsData]);

  // Configuration for charge types to display
  const chargeTypes = [
    { key: 'declaration', label: 'Declaration Charges' },
    { key: 'shipment', label: 'Shipment Charges' },
    { key: 'weight', label: 'Weight Charges' },
    { key: 'container', label: 'Container Charges' },
    { key: 'percentage', label: 'Percentage Charges' },
  ];

  const onSubmit = async (values) => {
    try {
      const reqBody = {
        shippingServiceId,
        startDate: format(values.startDate, 'yyyy-MM-dd'),
        endDate: format(values.endDate, 'yyyy-MM-dd'),
        sourceContractFlowId: values?.validityPeriodId ?? null,
      };

      await createValidityPeriod(reqBody);

      // Success toast
      openToast({
        toastType: 'success',
        title: 'Validity Period Created',
        description: 'The validity period has been created successfully',
      });

      reset();
      setOpen(false);
    } catch (error) {
      // Error handling is managed by the mutation's onError callback
      // Don't show custom error toast here to avoid duplication
      console.error('Failed to create validity period:', error);
    }
  };

  return (
    <FormContainer formContext={formContext}>
      <ModalConfirm
        title='Create Validity Period'
        open={open}
        onAccept={() => {
          handleSubmit(onSubmit)();
        }}
        onCancel={() => {
          setOpen(false);
        }}
        width={680}
      >
        <StyledBox className='min-h-[350px]'>
          <Stack
            rowGap={3}
            sx={{
              mt: '1rem',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                control={control}
                name='startDate'
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    label='Start Date'
                    value={field.value}
                    inputRef={field.ref}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    slotProps={{
                      popper: {
                        disablePortal: true,
                      },
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name='endDate'
                rules={{ required: true }}
                render={({ field }) => (
                  <DatePicker
                    label='End Date'
                    value={field.value}
                    inputRef={field.ref}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    slotProps={{
                      popper: {
                        disablePortal: true,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Stack>

          <div className='mt-20 p-12 rounded-12 bg-neutral-10 border border-neutral-40'>
            <div className='flex items-center gap-8 text-14'>
              <img src={IconInfo} alt='info' />
              <span>Copy charges from an existing validity period</span>
            </div>
            <div className='mt-12'>
              <Controller
                name='validityPeriodId'
                control={control}
                render={({ field }) => (
                  <StyledFormControl size='small'>
                    <InputLabel>Validity Period</InputLabel>
                    <Select
                      {...field}
                      input={<OutlinedInput label='Validity Period' />}
                      renderValue={(selected) => {
                        if (!selected) return null;
                        const selectedOption = options?.find((option) => option.value === selected);
                        return selectedOption?.label || '';
                      }}
                      MenuProps={{
                        slotProps: {
                          root: {
                            sx: { zIndex: 9999 },
                          },
                        },
                      }}
                    >
                      {options?.map((option) => (
                        <StyledMenuItem key={option.value} value={option.value}>
                          {option.label}
                        </StyledMenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                )}
              />
            </div>

            {/* Display validity period details when selected */}
            {isLoadingDetails && selectedValidityPeriodId && (
              <div className='mt-16 flex justify-center'>
                <CircularProgress size={24} />
              </div>
            )}

            {!isLoadingDetails && selectedContract && selectedContract.chargeCountOverview && (
              <div className='mt-12 py-24 bg-white border border-neutral-40 rounded-8'>
                <div className='flex flex-wrap justify-between'>
                  {chargeTypes.map((chargeType) => {
                    // Map chargeType keys to contract chargeCountOverview properties
                    const chargeCountMap = {
                      declaration: selectedContract.chargeCountOverview.contractDeclarationChargeCount || 0,
                      shipment: selectedContract.chargeCountOverview.contractShipmentChargeCount || 0,
                      weight: selectedContract.chargeCountOverview.contractWeightChargeCount || 0,
                      container: selectedContract.chargeCountOverview.contractContainerChargeCount || 0,
                      percentage: selectedContract.chargeCountOverview.contractPercentageChargeCount || 0,
                    };

                    return (
                      <div
                        key={chargeType.key}
                        className='relative flex items-center gap-8 px-16 w-1/5 after:absolute after:top-0 after:right-0 after:w-1 after:h-full after:bg-neutral-20 last:after:hidden'
                      >
                        <div className='text-24 font-bold'>{chargeCountMap[chargeType.key]}</div>
                        <div className='text-10 text-neutral-60'>{chargeType.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </StyledBox>
      </ModalConfirm>
    </FormContainer>
  );
};
